import { type AdminCloneJobBodyDto, JobsAdminService } from 'app/API';
import { cancelOnSignal } from 'app/API/core/CancelablePromise';
import type { TCall } from 'app/queries/types/TCall';
import type { TAdminCloneRequestOptions } from '../types/TAdminCloneRequestOptions';

const MAX_POLL_ATTEMPTS = 100;
const POLL_INTERVAL = 3000;

export const enqueueAndWaitForAdminClone = async (
  call: TCall,
  requestBody: AdminCloneJobBodyDto,
  options: TAdminCloneRequestOptions,
): Promise<string> => {
  const { signal, onError } = options;
  signal.throwIfAborted();
  const response = await call(
    () => cancelOnSignal(JobsAdminService.adminCloneJobFromEnvironment({ requestBody }), signal),
    { dontRedirectOnError: true, onError },
  );
  if (!response.queueJobId) {
    throw new Error('Clone queue job not found');
  }
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
    signal.throwIfAborted();
    const status = await call(
      () =>
        cancelOnSignal(
          JobsAdminService.adminGetCloneJobFromEnvironmentStatus({ cloneQueueJobId: response.queueJobId }),
          signal,
        ),
      { dontRedirectOnError: true, onError: (message) => onError(`Polling error: ${message}`) },
    );
    if (status.success && status.clonedJobId) {
      return status.clonedJobId;
    }
    if (status.status === 'failed') {
      throw new Error(status.failedReason ?? 'Clone job failed');
    }
    if (status.status === 'not_found') {
      throw new Error('Clone queue job not found');
    }
    if (attempt + 1 === MAX_POLL_ATTEMPTS) {
      break;
    }
    await new Promise<void>((resolve, reject) => {
      const finish = () => {
        signal.removeEventListener('abort', abort);
        resolve();
      };
      const timer = setTimeout(finish, POLL_INTERVAL);
      const abort = () => {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
        reject(signal.reason);
      };
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) {
        abort();
      }
    });
  }
  throw new Error('Cloning timed out. Please check back later.');
};
